import { Injectable, inject } from '@angular/core';
import { InternalLinkService } from '@bling-fe/shared/services';
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  BLUR_COMMAND,
  COMMAND_PRIORITY_EDITOR,
  createEditor,
  LexicalEditor
} from 'lexical';
import { HeadingNode, QuoteNode, registerRichText } from '@lexical/rich-text';
import { ListItemNode, ListNode, registerList } from '@lexical/list';
import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  CHECK_LIST,
  ELEMENT_TRANSFORMERS,
  LINK,
  MULTILINE_ELEMENT_TRANSFORMERS,
  TEXT_FORMAT_TRANSFORMERS,
  TEXT_MATCH_TRANSFORMERS
} from '@lexical/markdown';
import { BehaviorSubject, Observable } from 'rxjs';
import { registerLink } from '../plugins/link.plugin';
import {
  ContentLinkMenuState,
  ContentLinkPayload,
  INSERT_CONTENT_LINK_COMMAND,
  registerContentLinkTrigger
} from '../plugins/content-link.plugin';
import { registerInternalLinkAtomicDeletion } from '../plugins/internal-link-atomic.plugin';
import { extractMeaningfulTextFromUrl, isValidUrl } from '../utils/link.utils';
import { EditorCommand, EditorFormatState, FormatCommandType } from '../types';

import {
  checkForLinkNode,
  determineBlockType,
  getTopLevelElement,
  updateTextFormatState
} from '../utils/editor-node.utils';

import {
  formatBold,
  formatHeading,
  formatItalic,
  formatList,
  formatParagraph,
  formatQuote,
  formatStrike,
  formatUnderline
} from '../utils/editor-format.utils';

import { shouldResetOnBlur } from '../utils/editor-events.utils';
import { CreateEditorArgs } from 'lexical/LexicalEditor';
import { LinkNode } from '@lexical/link';
import { OPEN_LINK_DIALOG_COMMAND } from '../plugins/link-dialog.plugin';

const MARKDOWN_TRANSFORMERS = [
  CHECK_LIST,
  LINK,
  ...ELEMENT_TRANSFORMERS,
  ...MULTILINE_ELEMENT_TRANSFORMERS,
  ...TEXT_FORMAT_TRANSFORMERS,
  ...TEXT_MATCH_TRANSFORMERS
];

const DEFAULT_EDITOR_FORMAT_STATE: EditorFormatState = {
  blockType: 'paragraph',
  isBold: false,
  isItalic: false,
  isStrikethrough: false,
  isUnderline: false,
  isCode: false,
  isLink: false
};

const EDITOR_CONFIG: CreateEditorArgs = {
  nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode],
  theme: {
    heading: {
      h1: 'h1',
      h2: 'h2',
      h3: 'h3'
    },
    text: {
      bold: 'editor-bold',
      italic: 'editor-italic',
      strikethrough: 'editor-strikethrough'
    },
    quote: 'editor-blockquote',
    link: 'editor-link',
    list: {
      ul: 'editor-list-ul',
      ol: 'editor-list-ol'
    }
  },
  onError: error => {
    console.error(error);
  }
};

// service is provided in the component so state is not shared between multiple instances
@Injectable()
export class MarkdownEditorService {
  private readonly internalLinkService = inject(InternalLinkService);

  private readonly editor: LexicalEditor;
  private editorListeners: (() => void)[] = [];
  private currentValue = '';

  private formatStateSubject = new BehaviorSubject<EditorFormatState>(
    DEFAULT_EDITOR_FORMAT_STATE
  );

  formatState$: Observable<EditorFormatState> =
    this.formatStateSubject.asObservable();

  constructor() {
    this.editor = createEditor(EDITOR_CONFIG);
  }

  /**
   * Get the current format state
   */
  getFormatState(): EditorFormatState {
    return this.formatStateSubject.value;
  }

  /**
   * Get direct access to the editor instance
   */
  getEditor(): LexicalEditor {
    return this.editor;
  }

  /**
   * Activates the inline "#" content-link trigger and returns its menu state.
   */
  registerContentLinkTrigger(): Observable<ContentLinkMenuState> {
    const plugin = registerContentLinkTrigger(this.editor);
    this.editorListeners.push(plugin.unregister);
    return plugin.state$;
  }

  insertContentLink(payload: ContentLinkPayload): void {
    this.editor.dispatchCommand(INSERT_CONTENT_LINK_COMMAND, payload);
  }
  /**
   * Initialize the editor with the given root element
   */
  initializeEditor(rootElement: HTMLElement): void {
    this.editor.setRootElement(rootElement);
    this.registerPlugins();
    this.registerUpdateListener();
    this.registerBlurHandler();
  }

  /**
   * Register core editor plugins
   */
  private registerPlugins(): void {
    this.editorListeners.push(
      registerRichText(this.editor),
      registerList(this.editor),
      registerLink(this.editor, isValidUrl, undefined, url =>
        this.resolveLinkText(url)
      ),
      registerInternalLinkAtomicDeletion(this.editor, url =>
        this.isInternalUrl(url)
      )
    );
  }

  private isInternalUrl(url: string): boolean {
    try {
      return this.internalLinkService.isInternal(new URL(url));
    } catch {
      return false;
    }
  }

  // For an internal link to a known section, keep the bare URL as the text so
  // the render path translates it to the section label ("Gemeinschaft") live,
  // tracking the viewer's tenant translation overrides instead of freezing the
  // label at insertion time. Other links get a readable fragment of the URL.
  private resolveLinkText(url: string): string {
    try {
      if (this.internalLinkService.hasRouteLabel(new URL(url))) {
        return url;
      }
    } catch {
      // fall through to the default extraction below
    }
    return extractMeaningfulTextFromUrl(url);
  }

  /**
   * Register update listener to track content changes
   */
  private registerUpdateListener(): void {
    this.editorListeners.push(
      this.editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          const markdown = $convertToMarkdownString(
            MARKDOWN_TRANSFORMERS,
            $getRoot()
          );

          if (this.currentValue !== markdown) {
            this.currentValue = markdown;
          }

          this.updateFormatState();
        });
      })
    );
  }

  /**
   * Register blur handler to reset format state
   */
  private registerBlurHandler(): void {
    this.editorListeners.push(
      this.editor.registerCommand(
        BLUR_COMMAND,
        payload => {
          if (shouldResetOnBlur(payload, this.editor.getRootElement())) {
            this.resetFormatState();
          }

          return false;
        },
        COMMAND_PRIORITY_EDITOR
      )
    );
  }

  /**
   * Destroy editor and clean up listeners
   */
  destroyEditor(): void {
    this.editorListeners.forEach(unsubscribe => unsubscribe());
    this.editorListeners = [];
  }

  /**
   * Get the current markdown value
   */
  getValue(): string {
    return this.currentValue;
  }

  /**
   * Update editor content with markdown
   */
  updateContent(markdown: string): void {
    this.currentValue = markdown || '';

    this.editor.update(() => {
      const root = $getRoot();
      if (markdown && markdown.trim()) {
        $convertFromMarkdownString(markdown, MARKDOWN_TRANSFORMERS, root);
      } else {
        // Clear editor if empty string
        root.clear();
      }
    });
  }

  /**
   * Reset format state to default
   */
  resetFormatState(): void {
    this.formatStateSubject.next(DEFAULT_EDITOR_FORMAT_STATE);
  }

  /**
   * Update format state based on current selection
   */
  updateFormatState(): void {
    this.editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      const formatState = { ...this.formatStateSubject.value };
      const anchorNode = selection.anchor.getNode();
      const element = getTopLevelElement(anchorNode);

      formatState.isLink = checkForLinkNode(selection);
      formatState.blockType = determineBlockType(element, anchorNode);
      updateTextFormatState(selection, formatState);

      this.formatStateSubject.next(formatState);
    });
  }

  /**
   * Execute a format command
   */
  executeFormatCommand(command: EditorCommand): void {
    const currentState = this.getFormatState();

    switch (command.type) {
      case FormatCommandType.BOLD:
        formatBold(this.editor);
        break;
      case FormatCommandType.ITALIC:
        formatItalic(this.editor);
        break;
      case FormatCommandType.STRIKETHROUGH:
        formatStrike(this.editor);
        break;
      case FormatCommandType.UNDERLINE:
        formatUnderline(this.editor);
        break;
      case FormatCommandType.LINK:
        this.editor.dispatchCommand(OPEN_LINK_DIALOG_COMMAND, undefined);
        break;
      case FormatCommandType.HEADING:
        formatHeading(this.editor, command.payload, currentState.blockType);
        break;
      case FormatCommandType.QUOTE:
        formatQuote(this.editor, currentState.blockType);
        break;
      case FormatCommandType.BULLET_LIST:
        formatList(this.editor, 'bullet', currentState.blockType);
        break;
      case FormatCommandType.NUMBERED_LIST:
        formatList(this.editor, 'number', currentState.blockType);
        break;
      case FormatCommandType.PARAGRAPH:
        formatParagraph(this.editor);
        break;
    }
  }
}
